import React, { useEffect, useMemo, useState } from 'react';
import useHttp from './utils/hooks/use-http';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { AxiosRequestConfig, AxiosResponse } from 'axios';

import AppView from './App.view';

import { useLocation } from 'react-router';
import { QueryParams } from './constants/QueryParams';

import { serverAxios } from './utils/axios';

import * as fromApp from './store/app';
import * as authActions from './store/actions/auth';
import * as settingsActions from './store/actions/settings';

import specs from './specs';
import routes from 'routes';

import applyStyles from './utils/applyStyles';
import { ISpecs } from './models/specs/specs';
import { LocalStorage } from './constants/StorageConstants';
import { GetSupplierNameResponse } from './models/responses/supplier';
import { ISupplier } from './models/supplier';

interface PropsFromState {
  isAuthenticated: boolean;
  specs: ISpecs | null;
}

interface PropsFromDispatch {
  logout: () => authActions.Logout;
  setSpecs: (specs: ISpecs) => settingsActions.ISetSpecs;
  setSupplier: (supplierDetails: ISupplier) => settingsActions.ISetSupplier;
  autoLogin: () => authActions.AutoLogin;
}

interface Props extends PropsFromState, PropsFromDispatch { }

  
const App: React.FC<Props> = (props: React.PropsWithChildren<Props>) => {
  const [client, setClient] = useState<boolean>(true);
  const http = useHttp();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [supplierWrongState, setSupplierWrongState] = useState<boolean>(false);

  useEffect(() => {
    const client = queryParams.get(QueryParams.Client);

    if (!client && !localStorage.getItem(LocalStorage.Client)) {
      setClient(() => false);
      return;
    }

    if (client || localStorage.getItem(LocalStorage.Client)) {
      const specsConfig = specs.find((specs: ISpecs) => specs.client === (client || localStorage.getItem(LocalStorage.Client)));

      if (specsConfig) {
        props.setSpecs(specsConfig);
        localStorage.setItem(LocalStorage.Client, specsConfig.client);
      }
    }

    setClient(() => !!props.specs);
  }, [queryParams, props.specs]);

  const supplier = useMemo(() => {
    const paramSupplier = queryParams.get(QueryParams.Supplier);

    if (!paramSupplier) {
      return localStorage.getItem(LocalStorage.Supplier);
    }

    localStorage.setItem(LocalStorage.Supplier, paramSupplier);

    return paramSupplier;
  }, [queryParams, localStorage]);

  useEffect(() => {
    //init default parameters of supplier
    props.setSupplier({ name: supplier || '', pax: 1, multiplePaxDetails: false });

    if (supplier) {
      http.supplier
        .getSupplierName(supplier)
        .then((response: GetSupplierNameResponse) => {
          if (response.supplier) {
            const supplier: ISupplier = { name: response.supplier, pax: response.pax, multiplePaxDetails: response.multiplePaxDetails };

            props.setSupplier(supplier);
          }
        })
        .catch(() => {
          setSupplierWrongState(() => true);
        });
    }
  }, [supplier, setSupplierWrongState]);

  useEffect(() => {
    const token: string | null = localStorage.getItem(LocalStorage.Token);

    if (!token) {
      return;
    }

    props.autoLogin();
  }, [props.autoLogin]);

  /**
   * Add local storage token to the server requests
   */
  useEffect(() => {
    let cleanup: undefined | (() => void);

    if (props.isAuthenticated) {
      const tokenInterceptor = serverAxios.interceptors.request.use((request: AxiosRequestConfig): AxiosRequestConfig => {
        if (request.method === 'post') {
          request.data = {
            ...request.data,
            token: localStorage.getItem(LocalStorage.Token),
          };
        }
        return request;
      });

      cleanup = () => {
        serverAxios.interceptors.request.eject(tokenInterceptor);
      };
    }

    return cleanup;
  }, [props.isAuthenticated]);

  /**
   * Listen to requests response to logout the user if token is invalid
   */
  useEffect(() => {
    if (props.isAuthenticated) {
      const logoutInterceptor = serverAxios.interceptors.response.use((response: AxiosResponse): AxiosResponse => {
        if (response.data.error === 'invalid token') {
          props.logout();
        }

        return response;
      });

      return () => {
        serverAxios.interceptors.response.eject(logoutInterceptor);
      };
    }
  }, [props.logout, props.isAuthenticated]);

  /**
   * Apply styling according to the spec
   */
  useEffect(() => {
    if (props.specs) {
      applyStyles(props.specs.styles);
    }
  }, [props.specs]);

  return <AppView client={client} routes={routes} supplierWrongState={supplierWrongState}></AppView>;
};

App.displayName = 'App';
App.defaultProps = {};

const mapStateToProps = (state: fromApp.AppState) => {
  return {
    isAuthenticated: !!state.auth.user,
    specs: state.settings.specs,
  };
};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => {
  return {
    logout: (): authActions.Logout => dispatch(authActions.logout()),
    autoLogin: (): authActions.AutoLogin => dispatch(authActions.autoLogin()),
    setSpecs: (specs: ISpecs): settingsActions.ISetSpecs => dispatch(settingsActions.setSpecs(specs)),
    setSupplier: (supplierDetails: ISupplier): settingsActions.ISetSupplier => dispatch(settingsActions.setSupplier(supplierDetails)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(App);
